import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { DOMAIN_DEFAULTS } from '@/lib/domainDefaults';

/**
 * Google Analytics component with dual tracking support
 * 
 * Default behavior:
 * - Loads Tax.Network GA tracking for all users
 * 
 * Brazil branding context (tributario.com.br domain):
 * - ALSO loads Tributário GA tracking (dual tracking)
 * - Tracks to both Tax.Network and Tributário properties simultaneously
 */

// Google Analytics Measurement IDs
const TAX_NETWORK_GA_ID = 'G-2BP3GRX016';
const TRIBUTARIO_UA_ID = 'UA-2738438-1';
const TRIBUTARIO_GA4_ID = 'GT-TWZGVLS';
const GTAG_SCRIPT_ID = 'google-gtagjs';

export const GoogleAnalytics = () => {
  const location = useLocation();
  const isBrazilBranding = DOMAIN_DEFAULTS.isTributario;
  const isProduction = DOMAIN_DEFAULTS.isTaxNetwork || DOMAIN_DEFAULTS.isTributario;

  useEffect(() => {
    if (!isProduction) return;

    // Initialize dataLayer and gtag
    window.dataLayer = window.dataLayer || [];
    
    if (!window.gtag) {
      window.gtag = (...args: unknown[]) => {
        window.dataLayer.push(args);
      };
    }

    // Always load Tax.Network Google Analytics
    if (!document.getElementById(GTAG_SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = GTAG_SCRIPT_ID;
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${TAX_NETWORK_GA_ID}`;
      document.head.appendChild(script);
      
      window.gtag('js', new Date());
      window.gtag('config', TAX_NETWORK_GA_ID);
    }

    // Additionally load Tributario tracking when on Brazil site
    if (isBrazilBranding) {
      window.gtag('set', 'linker', {
        domains: ['tributario.com.br']
      });
      
      window.gtag('js', new Date());
      window.gtag('set', 'developer_id.dZTNiMT', true);
      
      window.gtag('set', 'user_properties', {
        tributario_user_id: '0',
        tributario_page_id: window.location.pathname,
        tributario_page_url: window.location.href
      });
      
      window.gtag('config', TRIBUTARIO_UA_ID, {
        anonymize_ip: true
      });
      
      window.gtag('config', TRIBUTARIO_GA4_ID);
    }
  }, [isBrazilBranding, isProduction]);

  // Track SPA route changes as page views
  useEffect(() => {
    if (!isProduction) return;

    if (window.gtag) {
      const pagePath = location.pathname + location.search;
      window.gtag('config', TAX_NETWORK_GA_ID, { page_path: pagePath });
      if (isBrazilBranding) {
        window.gtag('config', TRIBUTARIO_GA4_ID, { page_path: pagePath });
      }
    }
  }, [location.pathname, location.search, isBrazilBranding, isProduction]);

  return null;
};

// Extend Window interface for gtag
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}
